/**
 * Field Mode Context
 * Provides app-wide state for Field Mode (simplified mobile interface)
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { syncManager } from '../utils/syncManager';
import { offlineDB } from '../utils/offlineDB';

const FieldModeContext = createContext(null);

export const useFieldMode = () => {
  const context = useContext(FieldModeContext);
  if (!context) {
    throw new Error('useFieldMode must be used within a FieldModeProvider');
  }
  return context;
};

export const FieldModeProvider = ({ children }) => {
  const [isFieldMode, setIsFieldMode] = useState(() => {
    return localStorage.getItem('fieldMode') === 'true';
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({
    pendingCount: 0,
    failedCount: 0,
    isSyncing: false,
    lastSync: null
  });

  // Initialize offline DB
  useEffect(() => {
    offlineDB.init().catch(console.error);
  }, []);

  // Subscribe to sync events
  useEffect(() => {
    const unsubscribe = syncManager.subscribe((event) => {
      if (event.type === 'online') setIsOnline(true);
      if (event.type === 'offline') setIsOnline(false);
      if (event.type === 'sync_start') {
        setSyncStatus(prev => ({ ...prev, isSyncing: true }));
      }
      if (event.type === 'sync_complete') {
        setSyncStatus(prev => ({
          ...prev,
          isSyncing: false,
          pendingCount: event.remaining,
          lastSync: new Date().toISOString()
        }));
      }
      if (event.type === 'item_queued') {
        updateSyncStatus();
      }
    });

    // Start periodic sync when in field mode
    if (isFieldMode) {
      syncManager.startPeriodicSync(30000);
    }

    return () => {
      unsubscribe();
      syncManager.stopPeriodicSync();
    };
  }, [isFieldMode]);

  const updateSyncStatus = async () => {
    const status = await syncManager.getSyncStatus();
    setSyncStatus(status);
  };

  const toggleFieldMode = () => {
    const newValue = !isFieldMode;
    setIsFieldMode(newValue);
    localStorage.setItem('fieldMode', newValue.toString());
    
    if (newValue) {
      syncManager.startPeriodicSync(30000);
    } else {
      syncManager.stopPeriodicSync();
    }
  };

  const forceSync = async () => {
    await syncManager.syncAll();
    await updateSyncStatus();
  };

  // Check if device is mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                   window.innerWidth < 768;

  const value = {
    isFieldMode,
    toggleFieldMode,
    isOnline,
    isMobile,
    syncStatus,
    forceSync,
    updateSyncStatus,
    syncManager,
    offlineDB
  };

  return (
    <FieldModeContext.Provider value={value}>
      {children}
    </FieldModeContext.Provider>
  );
};

export default FieldModeContext;
